import {
  Link,
  useLoaderData,
  useRevalidator,
  useRouteError,
} from "react-router-dom";
import GenericErrorDisplay, { useSetError } from "../generic-error";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faClose,
  faDownload,
  faFile,
  faInfinity,
  faSadTear,
} from "@fortawesome/free-solid-svg-icons";
import TransmedReceivingDetail from "../../dtos/transmed/process/receivingDetail";
import { externalRedirectUrl } from "../page-redirect";
import { IMAGE_HOST } from "../../server/config";
import TransmedProcessService from "../../service/transmed/process";
import TransmedAdmin from "../../dtos/transmed/process/admin";
import { useState } from "react";
import TextInput from "../../components/inputs/text";
import { CircularProgress } from "@mui/material";
import { CSVLink } from "react-csv";
import TransmedReceivingService from "../../service/transmed/process/receiving";
import TransmedRegistrationPaymentService from "../../service/transmed/process/registration/payment";
import { PublicTransmedRegistration } from "../../dtos/transmed/process/registration/registration";

export const path = "transmed/incomings";

export async function loader() {
  const admin = new TransmedProcessService().read.admin();
  const receivings = new TransmedReceivingService().read.incomings();
  const registrations =
    new TransmedRegistrationPaymentService().read.incomings();
  return [await admin, await receivings, await registrations];
}

function Registration({
  registration,
}: {
  registration: PublicTransmedRegistration;
}) {
  const [admin, ,] = useLoaderData() as [
    TransmedAdmin,
    TransmedReceivingDetail[],
    PublicTransmedRegistration[]
  ];

  const [message, setMessage] = useState<string>(
    registration.rejection_message ?? ""
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const setError = useSetError();
  const revalidator = useRevalidator();

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();

        setIsLoading(true);
        setError("");
        try {
          await new TransmedRegistrationPaymentService().update.reject(
            registration.id,
            message.trim()
          );
        } catch (e) {
          setError(e);
        } finally {
          revalidator.revalidate();
          setIsLoading(false);
        }
      }}
      className="surface"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "var(--gap-small)",
        flexDirection: "column",
        alignItems: "stretch",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "var(--gap-small)",
          flexWrap: "wrap",
          flexGrow: 1,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "var(--gap-medium)",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <h3>
            {registration.user.first_name} {registration.user.last_name}
          </h3>
          <div>
            {registration.confirmed_lc_accomodation ? "cu" : "fără"} cazare
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: "var(--gap-small)",
            justifyContent: "flex-end",
            alignItems: "center",
            flexGrow: 1,
            flexWrap: "wrap",
          }}
        >
          {(isLoading || revalidator.state === "loading") && (
            <CircularProgress />
          )}
          <div
            className="label"
            style={{
              padding: "var(--gap-xs) var(--gap-small)",
              borderRadius: 1000,
              color:
                registration.payment_proof_validator === null
                  ? registration.payment_proof === null
                    ? "white"
                    : "var(--color-dark)"
                  : registration.rejection_message === null
                  ? "white"
                  : "white",
              backgroundColor:
                registration.payment_proof_validator === null
                  ? registration.payment_proof === null
                    ? "var(--color-dark)"
                    : "yellow"
                  : registration.rejection_message === null
                  ? "green"
                  : "var(--color-dark)",
            }}
          >
            {registration.payment_proof_validator === null
              ? registration.payment_proof === null
                ? "Neîncărcat"
                : "Nevalidat"
              : registration.rejection_message === null
              ? "Validat"
              : "Returnat"}
          </div>
          {registration.payment_proof !== null && (
            <Link
              to={externalRedirectUrl(
                IMAGE_HOST + (registration.payment_proof ?? "")
              )}
              className="button primary text"
            >
              <FontAwesomeIcon icon={faFile} /> Plata
            </Link>
          )}
          {registration.payment_proof_validator ? (
            <>
              Validator: <i>{registration.payment_proof_validator.public_id}</i>{" "}
              {registration.rejection_message && (
                <TextInput
                  value={registration.rejection_message}
                  onChange={(e) => {}}
                  label="Motiv refuz"
                />
              )}
            </>
          ) : (
            admin.validate && (
              <>
                <button
                  type="button"
                  className="button primary"
                  style={{ margin: 0, ...{ "--color": "green" } }}
                  disabled={
                    registration.payment_proof === null ||
                    registration.payment_proof_validator !== null ||
                    registration.rejection_message !== null ||
                    message.length > 0
                  }
                  onClick={async () => {
                    setIsLoading(true);
                    setError("");
                    try {
                      await new TransmedRegistrationPaymentService().update.validate(
                        registration.id
                      );
                    } catch (e) {
                      setError(e);
                    } finally {
                      revalidator.revalidate();
                      setIsLoading(false);
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faCheck} />
                </button>
                <button
                  type="submit"
                  className="button secondary"
                  style={{
                    margin: 0,
                    ...(registration.payment_proof_deadline! < new Date()
                      ? { "--color": "red" }
                      : {}),
                  }}
                  disabled={
                    registration.payment_proof === null ||
                    registration.payment_proof_validator !== null ||
                    registration.rejection_message !== null
                  }
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
                <TextInput
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  label="Motiv refuz"
                  disabled={
                    registration.payment_proof === null ||
                    registration.payment_proof_validator !== null ||
                    registration.rejection_message !== null
                  }
                  required
                />
              </>
            )
          )}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          gap: "var(--gap-small)",
          flexDirection: "row",
          justifyContent: "flex-start",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div>
          Nume: <b>{registration.user.last_name}</b>
        </div>
        <div>
          Prenume: <b>{registration.user.first_name}</b>
        </div>
        <div>
          Specializare:{" "}
          <b>
            {registration.user.is_medicine
              ? "Medicină"
              : registration.user.programme_title}
          </b>
        </div>
        <div>
          An: <b>{registration.user.year}</b>
        </div>
        <div>
          Serie: <b>{registration.user.series}</b>
        </div>
        <div>
          Grupă: <b>{registration.user.group}</b>
        </div>
        <div>
          Număr de înmatriculare: <b>{registration.user.registration_number}</b>
        </div>
        <div>
          Email: <b>{registration.user.email}</b>
        </div>
      </div>
    </form>
  );
}

function LcParticipants(props: {
  registrations: PublicTransmedRegistration[];
  receiving: TransmedReceivingDetail;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--gap-small)",
        alignItems: "stretch",
      }}
    >
      <h2>{props.receiving.sender.city}</h2>
      {props.registrations.map((registration) => (
        <Registration key={registration.id} registration={registration} />
      ))}
      <div className="label">
        Locuri rămase: Cu cazare:{" "}
        <b style={{ fontWeight: 600 }}>
          {props.receiving.accomodation_yes === null ? (
            <FontAwesomeIcon icon={faInfinity} />
          ) : (
            props.receiving.accomodation_yes -
            props.registrations.reduce(
              (prev, curr) => prev + (curr.confirmed_lc_accomodation ? 1 : 0),
              0
            )
          )}
        </b>
        , Fără cazare:{" "}
        <b style={{ fontWeight: 600 }}>
          {props.receiving.accomodation_no === null ? (
            <FontAwesomeIcon icon={faInfinity} />
          ) : (
            props.receiving.accomodation_no -
            props.registrations.reduce(
              (prev, curr) => prev + (curr.confirmed_lc_accomodation ? 0 : 1),
              0
            )
          )}
        </b>
      </div>
    </div>
  );
}

export function Component() {
  const [, receivings, registrations] = useLoaderData() as [
    TransmedAdmin,
    TransmedReceivingDetail[],
    PublicTransmedRegistration[]
  ];

  return (
    <>
      <h1 style={{ marginBottom: "var(--gap-big)", wordBreak: "break-word" }}>
        Participanți transmed
      </h1>
      <p>
        Lista cu participanți din celelalte centre care vor veni în transmed.
      </p>
      <CSVLink
        data={registrations
          .sort((a, b) => a.user.year - b.user.year)
          .map((element, index) => ({
            "Nr. crt.": (index + 1).toFixed(0),
            Centru: element.user.lc.city,
            "Id public": element.user.public_id,
            Email: element.user.email,
            Nume: element.user.last_name,
            Prenume: element.user.first_name,
            An: element.user.year,
            Cazare: element.confirmed_lc_accomodation
              ? "Cu cazare"
              : "Fără cazare",
          }))}
        filename="participanti.csv"
        target="_blank"
        className="primary button"
        style={{ alignSelf: "flex-start" }}
      >
        <FontAwesomeIcon icon={faDownload} /> Descarcă CSV
      </CSVLink>
      {registrations.length <= 0 && receivings.length <= 0 ? (
        <span>
          <FontAwesomeIcon icon={faSadTear} color="var(--color-fasmr-blue)" />{" "}
          Niciun participant nu a confirmat încă locul în centrul tău.
        </span>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--gap-medium)",
            alignItems: "stretch",
          }}
        >
          {receivings.map((receiving, index) => (
            <LcParticipants
              key={index}
              registrations={registrations.filter(
                (r) => r.user.lc.id === receiving.sender.id
              )}
              receiving={receiving}
            />
          ))}
          <hr />
          {registrations
            .filter(
              (registration) =>
                receivings.find(
                  (receiving) => registration.user.lc.id === receiving.sender.id
                ) === undefined
            )
            .map((registration) => (
              <Registration key={registration.id} registration={registration} />
            ))}
        </div>
      )}
    </>
  );
}
Component.displayName = "page-account-transmed-incomings";

export function ErrorBoundary() {
  const error = useRouteError();
  return <GenericErrorDisplay error={error} />;
}
ErrorBoundary.displayName = "page-account-transmed-incomings-error";
